/**
 * formatters.js
 * Utility functions for consistent number/currency/date formatting in the UI.
 */

/** Format a number as currency with the store's currency code. */
export function formatCurrency(amount, currency = 'CAD') {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

/** Format a plain number with thousands separators. */
export function formatNumber(n, decimals = 0) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

/**
 * Format inventory runway days into a human-readable string.
 * null = infinite (dead stock with no sales).
 */
export function formatRunway(days) {
  if (days === null || days === undefined) return '∞ (no sales)';
  if (days === 0) return 'Out of stock';
  if (days >= 365) return `${Math.round(days / 365)}y ${Math.round((days % 365) / 30)}m`;
  if (days >= 30) return `${Math.round(days / 30)}m ${days % 30}d`;
  return `${days}d`;
}

/** Format a daily sell rate nicely. */
export function formatRate(rate) {
  if (!rate) return '0 / day';
  if (rate >= 1) return `${formatNumber(rate, 1)} / day`;
  const weekly = rate * 7;
  if (weekly >= 1) return `${formatNumber(weekly, 1)} / wk`;
  return `${formatNumber(rate * 30, 1)} / mo`;
}

/** Format an ISO date string to a short readable date. */
export function formatDate(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Friendly period label for display. */
export function periodLabel(period) {
  const map = {
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    '6m': 'Last 6 months',
    '12m': 'Last 12 months',
    '1y': 'Last year',
  };
  return map[period] || period;
}

/**
 * Convert a 2-letter ISO country code to a flag emoji.
 * Each letter is mapped to its regional indicator symbol.
 *
 * @param {string} code - ISO 3166-1 alpha-2 country code (e.g. 'CA', 'US')
 * @returns {string} flag emoji or empty string if code is invalid
 */
export function countryCodeToFlag(code) {
  if (!code || code.length !== 2) return '';
  return code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0)))
    .join('');
}

/**
 * Format a percentage change value with arrow indicator.
 * e.g. +12.5 → "▲ +12.5%", -8.2 → "▼ -8.2%", 0 → "0%"
 *
 * @param {number} pct - percentage change value
 * @returns {string} formatted change string
 */
export function formatChange(pct) {
  if (pct === null || pct === undefined) return '—';
  if (pct === 0) return '0%';
  const arrow = pct > 0 ? '▲' : '▼';
  const sign = pct > 0 ? '+' : '';
  return `${arrow} ${sign}${pct}%`;
}

/**
 * Return a Polaris badge tone based on change direction.
 *
 * @param {number} pct - percentage change value
 * @returns {string} 'success' | 'critical' | 'subdued'
 */
export function getChangeTone(pct) {
  if (pct > 0) return 'success';
  if (pct < 0) return 'critical';
  return 'subdued';
}

/**
 * Format a value as a percentage string, e.g. "45.2%".
 *
 * @param {number} value - the percentage value (0–100 scale)
 * @param {number} [decimals=1] - decimal places
 * @returns {string} formatted percentage
 */
export function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Runway status for badge colouring.
 * Returns a Polaris badge status string.
 */
export function runwayStatus(days, overstockDays = 90) {
  if (days === null) return 'critical'; // dead stock
  if (days === 0) return 'warning';
  if (days > overstockDays) return 'attention'; // overstock
  if (days < 14) return 'critical'; // almost out
  if (days < 30) return 'warning'; // low stock
  return 'success'; // healthy
}
